import React, { useCallback, useMemo, useRef, useState } from 'react';

import { ContentInput } from 'hooks/useContentMetricIds';
import {
  AnswerSourceFilter,
  InsightsStudioContext,
  StudioBlockAdd,
  StudioBlockArrange,
  StudioBlockEdit,
  StudioBlockPending,
} from 'services/streamInsightsQuery';

import {
  INSIGHTS_VALUES_SOURCE_PRIORITY,
  InsightsGadgetScopeFragment,
  InsightsGadgetValues,
  InsightsValuesSource,
  InsightsView,
  InsightsViewValues,
} from './viewTypes';

// What a card's value getter is told when it runs. `pinnedLabels` names rows the
// digest must include even if head/tail sampling would drop them — the value
// the user just clicked ("Explain this value") is usually mid-table.
export interface InsightsGetValuesOptions {
  pinnedLabels?: string[];
}

export type InsightsEntityType = 'report' | 'dashboard' | 'workspace';

// The content the user is currently viewing, published by the leaf Dashboard /
// Report components (which render everywhere — standalone, workspace tabs, apps)
// and read by the globally-mounted InsightsChatProvider. content is kept so the
// chat can extract the entity's metric ids via useContentMetricIds.
export interface InsightsContent {
  entityType: InsightsEntityType;
  entityId: string;
  entityName: string;
  content: ContentInput;
}

// What applying an agent's Studio edit came to. `rejected` lists what was
// left out (e.g. a metric id that doesn't exist); `missing` is why the block
// still can't draw.
// Why a block the agent added isn't on the canvas
export type StudioNotAddedReason = 'noMetrics' | 'noRoom' | 'exists';

export interface StudioEditOutcome {
  isApplied: boolean;
  rejected: string[];
  // Set when a new block wasn't added
  reason?: StudioNotAddedReason;
  missing?: string;
  // Applied, but with a side effect worth knowing, e.g. a {placeholder}
  // left naming a metric the block no longer has
  warnings?: string[];
}

// The Studio builder on screen, as the chat sees it: what to tell the agent,
// and how to apply (and take back) its edits and new blocks.
export interface InsightsStudioBuilder {
  getContext: () => InsightsStudioContext;
  applyEdit: (edit: StudioBlockEdit) => StudioEditOutcome;
  // knownMetricIds: metrics that exist but may not be loaded yet (one just
  // created from a proposal). isStandalone: added on its own, not as part of
  // the answer's build, so no row is stretched for it.
  addBlock: (
    block: StudioBlockAdd,
    opts?: { knownMetricIds?: string[]; isStandalone?: boolean },
  ) => StudioEditOutcome;
  // Moving, resizing or removing a block on the canvas
  arrange: (arrange: StudioBlockArrange) => StudioEditOutcome;
  // A placeholder where a block the agent is writing will go, until its
  // addBlock (same toolUseId) or cancelPending; clearPending drops any left
  // once the agent has moved on
  addPending: (pending: StudioBlockPending) => void;
  cancelPending: (toolUseId: string) => void;
  clearPending: () => void;
  canUndo: (editId: string) => boolean;
  undo: (editId: string) => void;
}

export interface InsightsContentContextType {
  current: InsightsContent | null;
  setCurrent: (content: InsightsContent) => void;
  // Clears only when the given id is still the current one, so a component
  // unmounting after a newer one registered doesn't wipe the newer entity.
  clearCurrent: (entityId: string) => void;

  // The LIVE view of `current` — the effective date range, filters and gadget
  // dimensions on screen. A separate slot rather than a field on InsightsContent
  // because the two are published from different depths of the tree: the entity
  // registers above the metric/vis gates (so it survives loading), the view
  // registers inside FilterPlatesProvider (so it can read live filters).
  // Keyed by entityId for the same race guard as clearCurrent.
  currentView: { entityId: string; view: InsightsView } | null;
  setCurrentView: (entityId: string, view: InsightsView) => void;
  clearCurrentView: (entityId: string) => void;

  // The numbers actually rendered. Deliberately NOT React state: gadget data
  // settles independently on every refetch, and pushing that through a context
  // value would re-render every consumer for data nobody has asked for yet.
  // Instead each card registers a getter; the chat pulls them at send time and
  // the digest is computed lazily inside the getter.
  registerViewValues: (args: {
    entityId: string;
    cardId: string;
    // Kept apart per source so a gadget whose data resolves through both hooks
    // doesn't have one clobber the other; reconciled on read.
    source: InsightsValuesSource;
    getValues: (opts?: InsightsGetValuesOptions) => InsightsGadgetValues;
  }) => () => void;
  // `pinned` forwards pinnedLabels to ONE card's getters — the focused gadget —
  // so every other digest keeps its normal head/tail sample.
  getViewValues: (
    entityId: string,
    opts?: { pinned?: { cardId: string; labels: string[] } },
  ) => {
    [cardId: string]: InsightsGadgetValues;
  };

  // A dashboard gadget's resolved scope (date range + filters), captured the same
  // ref-registry way as the values above and for the same reason: a dashboard has
  // no view-level scope, so each gadget resolves its own deep in the tree, and
  // pushing that through React state would re-render for data nobody has asked for
  // yet. Keyed by cardId (one scope per card). No source split — unlike values, a
  // gadget's scope has a single source of truth.
  registerGadgetScope: (args: {
    entityId: string;
    cardId: string;
    getScope: () => InsightsGadgetScopeFragment;
  }) => () => void;
  getGadgetScopes: (entityId: string) => {
    [cardId: string]: InsightsGadgetScopeFragment;
  };

  // Reverse channel: the chat is mounted globally and can't reach a report's
  // FilterPlatesContext.setDrillDowns (that lives inside the Report subtree). The
  // report registers an "apply these filters" callback keyed by entityId, mirroring
  // registerViewValues — registering causes no render, and only the on-screen,
  // non-popup report registers, so applyFilters for any other id is a silent no-op.
  registerApplyFilters: (
    entityId: string,
    apply: (filters: AnswerSourceFilter[]) => void,
  ) => () => void;
  applyFilters: (entityId: string, filters: AnswerSourceFilter[]) => void;

  // Reverse channel into the Studio builder, the same ref-registry way: the
  // chat reads the canvas at send time and hands the agent's edits back to it
  // as they stream in. Only one Studio is ever on screen.
  registerStudioBuilder: (builder: InsightsStudioBuilder) => () => void;
  getStudioBuilder: () => InsightsStudioBuilder | null;
}

export const InsightsContentContext =
  React.createContext<InsightsContentContextType>({
    current: null,
    setCurrent: () => undefined,
    clearCurrent: () => undefined,
    currentView: null,
    setCurrentView: () => undefined,
    clearCurrentView: () => undefined,
    registerViewValues: () => () => undefined,
    getViewValues: () => ({}),
    registerGadgetScope: () => () => undefined,
    getGadgetScopes: () => ({}),
    registerApplyFilters: () => () => undefined,
    applyFilters: () => undefined,
    registerStudioBuilder: () => () => undefined,
    getStudioBuilder: () => null,
  });

const InsightsContentProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const [current, setCurrentState] = useState<InsightsContent | null>(null);
  const [currentView, setCurrentViewState] = useState<{
    entityId: string;
    view: InsightsView;
  } | null>(null);

  const setCurrent = useCallback(
    (content: InsightsContent) => setCurrentState(content),
    [],
  );

  const clearCurrent = useCallback(
    (entityId: string) =>
      setCurrentState((prev) =>
        prev && prev.entityId === entityId ? null : prev,
      ),
    [],
  );

  const setCurrentView = useCallback(
    (entityId: string, view: InsightsView) =>
      setCurrentViewState({ entityId, view }),
    [],
  );

  const clearCurrentView = useCallback(
    (entityId: string) =>
      setCurrentViewState((prev) =>
        prev && prev.entityId === entityId ? null : prev,
      ),
    [],
  );

  // entityId -> "cardId::source" -> getter. A ref, so registering causes no render.
  const valueSourcesRef = useRef<
    Map<
      string,
      Map<string, (opts?: InsightsGetValuesOptions) => InsightsGadgetValues>
    >
  >(new Map());

  const registerViewValues = useCallback(
    ({
      entityId,
      cardId,
      source,
      getValues,
    }: {
      entityId: string;
      cardId: string;
      source: InsightsValuesSource;
      getValues: (opts?: InsightsGetValuesOptions) => InsightsGadgetValues;
    }) => {
      const key = `${cardId}::${source}`;
      const forEntity =
        valueSourcesRef.current.get(entityId) ??
        new Map<
          string,
          (opts?: InsightsGetValuesOptions) => InsightsGadgetValues
        >();
      forEntity.set(key, getValues);
      valueSourcesRef.current.set(entityId, forEntity);

      return () => {
        const stillThere = valueSourcesRef.current.get(entityId);
        if (!stillThere) {
          return;
        }
        // Only drop our own registration: a remounting card can re-register
        // under the same key before the old instance's cleanup runs.
        if (stillThere.get(key) === getValues) {
          stillThere.delete(key);
        }
        if (stillThere.size === 0) {
          valueSourcesRef.current.delete(entityId);
        }
      };
    },
    [],
  );

  const getViewValues = useCallback(
    (
      entityId: string,
      opts?: { pinned?: { cardId: string; labels: string[] } },
    ) => {
      const forEntity = valueSourcesRef.current.get(entityId);
      if (!forEntity) {
        return {};
      }

      const read = (
        key: string,
        getterOpts?: InsightsGetValuesOptions,
      ): InsightsGadgetValues => {
        const getValues = forEntity.get(key);
        if (!getValues) {
          return undefined;
        }
        try {
          return getValues(getterOpts);
        } catch {
          // A single misbehaving gadget must never break sending a question.
          return undefined;
        }
      };

      const cardIds = new Set(
        [...forEntity.keys()].map((key) => key.split('::')[0]),
      );

      const result: { [cardId: string]: InsightsGadgetValues } = {};
      cardIds.forEach((cardId) => {
        const pinned = opts?.pinned;
        const getterOpts =
          pinned && pinned.cardId === cardId
            ? { pinnedLabels: pinned.labels }
            : undefined;
        const candidates = INSIGHTS_VALUES_SOURCE_PRIORITY.map((source) =>
          read(`${cardId}::${source}`, getterOpts),
        ).filter(
          (values): values is NonNullable<InsightsGadgetValues> =>
            values !== undefined,
        );
        // Prefer a source that actually produced figures; fall back to a loading
        // marker so a mid-refetch gadget still reports why it has none.
        result[cardId] =
          candidates.find((values) => !('isLoading' in values)) ??
          candidates[0];
      });
      return result;
    },
    [],
  );

  // entityId -> cardId -> getter. A ref, so registering causes no render.
  const gadgetScopesRef = useRef<
    Map<string, Map<string, () => InsightsGadgetScopeFragment>>
  >(new Map());

  const registerGadgetScope = useCallback(
    ({
      entityId,
      cardId,
      getScope,
    }: {
      entityId: string;
      cardId: string;
      getScope: () => InsightsGadgetScopeFragment;
    }) => {
      const forEntity =
        gadgetScopesRef.current.get(entityId) ??
        new Map<string, () => InsightsGadgetScopeFragment>();
      forEntity.set(cardId, getScope);
      gadgetScopesRef.current.set(entityId, forEntity);

      return () => {
        const stillThere = gadgetScopesRef.current.get(entityId);
        if (!stillThere) {
          return;
        }
        // Only drop our own registration: a remounting card can re-register
        // under the same key before the old instance's cleanup runs.
        if (stillThere.get(cardId) === getScope) {
          stillThere.delete(cardId);
        }
        if (stillThere.size === 0) {
          gadgetScopesRef.current.delete(entityId);
        }
      };
    },
    [],
  );

  const getGadgetScopes = useCallback((entityId: string) => {
    const forEntity = gadgetScopesRef.current.get(entityId);
    if (!forEntity) {
      return {};
    }
    const result: { [cardId: string]: InsightsGadgetScopeFragment } = {};
    forEntity.forEach((getScope, cardId) => {
      try {
        result[cardId] = getScope();
      } catch {
        // A single misbehaving gadget must never break sending a question.
      }
    });
    return result;
  }, []);

  // entityId -> apply callback. A ref, so registering causes no render.
  const applyFiltersRef = useRef<
    Map<string, (filters: AnswerSourceFilter[]) => void>
  >(new Map());

  const registerApplyFilters = useCallback(
    (entityId: string, apply: (filters: AnswerSourceFilter[]) => void) => {
      applyFiltersRef.current.set(entityId, apply);
      return () => {
        // Only drop our own registration: a remounting report can re-register
        // before the old instance's cleanup runs.
        if (applyFiltersRef.current.get(entityId) === apply) {
          applyFiltersRef.current.delete(entityId);
        }
      };
    },
    [],
  );

  const applyFilters = useCallback(
    (entityId: string, filters: AnswerSourceFilter[]) => {
      applyFiltersRef.current.get(entityId)?.(filters);
    },
    [],
  );

  // A ref, so registering causes no render
  const studioBuilderRef = useRef<InsightsStudioBuilder | null>(null);

  const registerStudioBuilder = useCallback(
    (builder: InsightsStudioBuilder) => {
      studioBuilderRef.current = builder;
      return () => {
        // Only drop our own registration, as above
        if (studioBuilderRef.current === builder) {
          studioBuilderRef.current = null;
        }
      };
    },
    [],
  );

  const getStudioBuilder = useCallback(() => studioBuilderRef.current, []);

  const value = useMemo(
    () => ({
      current,
      setCurrent,
      clearCurrent,
      currentView,
      setCurrentView,
      clearCurrentView,
      registerViewValues,
      getViewValues,
      registerGadgetScope,
      getGadgetScopes,
      registerApplyFilters,
      applyFilters,
      registerStudioBuilder,
      getStudioBuilder,
    }),
    [
      current,
      setCurrent,
      clearCurrent,
      currentView,
      setCurrentView,
      clearCurrentView,
      registerViewValues,
      getViewValues,
      registerGadgetScope,
      getGadgetScopes,
      registerApplyFilters,
      applyFilters,
      registerStudioBuilder,
      getStudioBuilder,
    ],
  );

  return (
    <InsightsContentContext.Provider value={value}>
      {children}
    </InsightsContentContext.Provider>
  );
};

export type { InsightsView, InsightsViewValues, InsightsGadgetValues };

export default InsightsContentProvider;
